import type {
  ComponentVisibility,
  FormComponent,
  FormDSLContext,
  FormStep,
  ValidationRules,
} from '@commenda/types';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { PreviewFillHandler } from '../hooks/FormContext';
import {
  computeStepPrefill,
  type PrefillSkip,
  type StepPrefillPlan,
} from './preview-prefill';
import {
  findBrokenRequiredRules,
  findFieldLabelInStep,
  getRequiredFieldPresence,
  getStepTitle,
} from './preview-run-helpers';

export type EmptyCheckOutcome =
  | 'errors-fired' // required errors fired on an empty step — healthy
  | 'nothing-required' // step has no required fields — neutral
  | 'skipped-already-filled' // required fields all had values before the run — not testable
  | 'no-error-despite-required'; // empty required fields present, no error — misconfigured

/** One value the filler wrote, for the run report's "show test values". */
export interface FilledValue {
  label: string;
  value: unknown;
}

export interface StepRunReport {
  stepIndex: number;
  stepTitle: string;
  emptyCheck: EmptyCheckOutcome;
  filledCount: number;
  /** Fields the filler had a value for, filled or not — the "2 of 3" denominator. */
  candidateCount: number;
  filledValues: FilledValue[];
  skipped: PrefillSkip[];
  alreadyFilledCount: number;
  haltReason?: string;
  /**
   * Static lint findings: required rules that can never activate because their
   * `when` condition references a field that doesn't exist in the form. The
   * renderer treats such fields as optional, so no runtime check can catch
   * them — only this DSL-level scan.
   */
  brokenRequiredRules: string[];
}

export interface StepFillSummary {
  filledCount: number;
  candidateCount: number;
  alreadyFilledCount: number;
  skipped: PrefillSkip[];
}

export type RunStatus = 'idle' | 'running' | 'halted' | 'done';

type RunPhase =
  | 'idle'
  | 'probe'
  | 'fill'
  | 'settle'
  | 'post-validate'
  | 'done'
  | 'halted';

// Per-step budget for waiting on OS components' async data during a run:
// 15 attempts x 400ms ≈ 6s before unloaded fields are reported as skips.
const MAX_FILL_RETRIES_PER_STEP = 15;
const FILL_RETRY_DELAY_MS = 400;
// After filling, give component effects (OS sync, cascading writes) a moment
// to commit before validating — validating the instant after a fill judges a
// state that is still moving.
const STEP_SETTLE_MS = 2000;

interface UsePreviewTestRunnerArgs {
  isPreview: boolean;
  visibleSteps: FormStep[];
  currentStep: number;
  setCurrentStep: (step: number) => void;
  formData: Record<string, unknown>;
  dslContext?: FormDSLContext;
  updateField: (fieldName: string, value: unknown) => void;
  validateField: (
    fieldName: string,
    value: unknown,
    rules?: ValidationRules
  ) => string | null;
  isFieldVisible: (visibility?: ComponentVisibility) => boolean;
  isFieldDisabled: (fieldName?: string) => boolean;
  validateCurrentStep: () => { isValid: boolean; firstErrorField?: string };
  componentMap?: Record<string, FormComponent>;
  getPreviewFillHandler?: (fieldId: string) => PreviewFillHandler | undefined;
  /**
   * True while the renderer's Commenda OS prefill is fetching values for
   * OS-bound fields (AutoDetectField etc.). Those fields fill themselves via
   * sync effects when data lands, so validating before it settles produces
   * false halts on values that are seconds away.
   */
  isCommendaOSPrefilling?: boolean;
  /**
   * Clears a field's recorded validation error. Self-fill handlers update
   * values through their components' own internals, which never touch the
   * error map — so an error recorded by the empty-state probe would linger
   * and trip validateCurrentStep's lingering-error check even though the
   * field now holds a valid value.
   */
  clearFieldError?: (fieldId: string) => void;
  /** Current validation error message for a field, for precise halt reporting. */
  getFieldError?: (fieldId: string) => string | undefined;
  /**
   * Expands every module on the current step. Collapsed accordion modules
   * unmount their components (no handlers, no value sync), so both fill paths
   * expand the step before filling it.
   */
  expandCurrentStepModules?: () => void;
}

/**
 * Preview-only testing engine: a per-step "fill" action and a "fill & run to
 * end" runner that walks the whole form.
 *
 * The runner is a phase-driven state machine because filling a field is an
 * async React state update — validation can only observe filled values on the
 * next render. Each phase transition changes `runPhase` (and sometimes
 * `currentStep`), so a single effect keyed on those drives one transition per
 * committed render:
 *
 *   probe → fill → post-validate → (advance ⇒ probe | done | halted)
 *
 * `probe` runs validation on the untouched step and classifies the empty-state
 * outcome (the check a human tester skips once a fill button exists — including
 * flagging steps whose required fields fire no error). `post-validate` runs
 * validation after the fill and halts the walk at the first filled step that
 * still fails, leaving the user parked there with errors visible.
 */
export function usePreviewTestRunner({
  isPreview,
  visibleSteps,
  currentStep,
  setCurrentStep,
  formData,
  dslContext,
  updateField,
  validateField,
  isFieldVisible,
  isFieldDisabled,
  validateCurrentStep,
  componentMap,
  getPreviewFillHandler,
  isCommendaOSPrefilling,
  clearFieldError,
  getFieldError,
  expandCurrentStepModules,
}: UsePreviewTestRunnerArgs) {
  const [runPhase, setRunPhase] = useState<RunPhase>('idle');
  const [runReport, setRunReport] = useState<StepRunReport[] | null>(null);
  const [lastFillResult, setLastFillResult] = useState<StepFillSummary | null>(
    null
  );

  const reportRef = useRef<StepRunReport[]>([]);
  const pendingFillResultRef = useRef<StepFillSummary | null>(null);
  const pendingFilledValuesRef = useRef<FilledValue[]>([]);
  // Guards against a runaway loop if step bookkeeping ever fails to converge.
  const safetyCounterRef = useRef(0);
  // OS components load their data async; when a self-fill handler reports
  // `loading`, the runner re-attempts the step's fill on a short delay instead
  // of skipping fields that would fill fine a moment later. Bounded per step.
  const fillRetryCountRef = useRef(0);
  const [fillRetryTick, setFillRetryTick] = useState(0);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Background writes (OS prefill re-merges, wrapper sync effects) can leave
  // zombie entries in the error map for fields that now hold valid values.
  // Validation clears stale per-field errors as it re-checks them, so a
  // failing first pass gets one re-validation a tick later; only a second
  // failure is a real halt.
  const revalidatedRef = useRef(false);

  // Applies a plan: registry assignments go through updateField + validateField
  // (the exact pair a field wrapper runs on typing — validating clears errors
  // the empty-state probe recorded), then self-fill handlers are invoked and
  // their outcomes merged into the final counts.
  const executePlan = useCallback(
    (plan: StepPrefillPlan): StepFillSummary & {
      loadingFields: { fieldId: string; label: string }[];
    } => {
      plan.assignments.forEach((assignment) => {
        updateField(assignment.fieldId, assignment.value);
        validateField(assignment.fieldId, assignment.value, assignment.rules);
        // Repeatable-module assignments carry per-instance validations so
        // errors keyed `saveKey.index.fieldId` clear like typed input would.
        assignment.instanceFieldValidations?.forEach((instanceValidation) => {
          validateField(
            instanceValidation.fieldName,
            instanceValidation.value,
            instanceValidation.rules
          );
        });
      });

      let { filledFieldCount: filledCount, alreadyFilledCount } = plan;
      const skipped = [...plan.skipped];
      const loadingFields: { fieldId: string; label: string }[] = [];
      plan.handlerCandidates.forEach(({ fieldId, label }) => {
        const fillHandler = getPreviewFillHandler?.(fieldId);
        if (!fillHandler) {
          // Self-fill component not mounted yet (collapsed module just
          // expanded) — treat as loading so the retry window covers the mount.
          loadingFields.push({ fieldId, label });
          return;
        }
        const outcome = fillHandler();
        if (outcome.kind === 'filled') {
          filledCount += 1;
          clearFieldError?.(fieldId);
        } else if (outcome.kind === 'already-filled') {
          alreadyFilledCount += 1;
          // A probe error recorded against a value the component itself
          // considers present is stale; post-validation re-detects genuine
          // failures fresh, so clearing here can't mask a real one.
          clearFieldError?.(fieldId);
        }
        else if (outcome.kind === 'loading') loadingFields.push({ fieldId, label });
        else skipped.push({ fieldId, label, reason: outcome.reason });
      });

      return {
        filledCount,
        alreadyFilledCount,
        skipped,
        candidateCount: filledCount + skipped.length + loadingFields.length,
        loadingFields,
      };
    },
    [updateField, validateField, getPreviewFillHandler, clearFieldError]
  );

  const fillCurrentStep = useCallback(() => {
    // A report lingering from an earlier run supersedes the per-step fill note
    // in the panel — clear it so a manual fill shows its own feedback.
    setRunReport(null);
    expandCurrentStepModules?.();
    const plan = computeStepPrefill({
      step: visibleSteps[currentStep],
      formData,
      dslContext,
      isFieldVisible,
      isFieldDisabled,
      isPreview,
      hasFillHandler: (candidateFieldId) =>
        Boolean(getPreviewFillHandler?.(candidateFieldId)),
    });
    const executed = executePlan(plan);
    setLastFillResult({
      ...executed,
      skipped: [
        ...executed.skipped,
        ...executed.loadingFields.map(({ fieldId, label }) => ({
          fieldId,
          label,
          reason: 'still loading — click Fill again in a moment',
        })),
      ],
    });
  }, [
    visibleSteps,
    currentStep,
    formData,
    dslContext,
    isFieldVisible,
    isFieldDisabled,
    isPreview,
    executePlan,
    getPreviewFillHandler,
    expandCurrentStepModules,
  ]);

  // A fill result describes one step; once the user navigates away it would
  // mislead ("Pre-filled 4 of 4" shown on a different step), so clear it.
  // Runner phases drive currentStep too, but the run report supersedes the
  // per-step note in the panel, so clearing during a run is harmless.
  const lastFillStepRef = useRef(currentStep);
  useEffect(() => {
    if (lastFillStepRef.current !== currentStep) {
      lastFillStepRef.current = currentStep;
      setLastFillResult(null);
      fillRetryCountRef.current = 0;
      revalidatedRef.current = false;
    }
  }, [currentStep]);

  const runToEnd = useCallback(() => {
    if (settleTimerRef.current) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
    reportRef.current = [];
    pendingFillResultRef.current = null;
    safetyCounterRef.current = 0;
    // The per-step waiting budgets normally reset on step change, but a halted
    // run leaves them spent on the very step the next run starts from.
    fillRetryCountRef.current = 0;
    revalidatedRef.current = false;
    setRunReport(null);
    setLastFillResult(null);
    setRunPhase('probe');
  }, []);

  const stopRun = useCallback(() => {
    if (
      runPhase !== 'probe' &&
      runPhase !== 'fill' &&
      runPhase !== 'settle' &&
      runPhase !== 'post-validate'
    ) {
      return;
    }
    if (settleTimerRef.current) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
    setRunReport([...reportRef.current]);
    setRunPhase('halted');
  }, [runPhase]);

  useEffect(() => {
    if (
      runPhase === 'idle' ||
      runPhase === 'done' ||
      runPhase === 'halted'
    ) {
      return;
    }

    safetyCounterRef.current += 1;
    if (safetyCounterRef.current > visibleSteps.length * 4 + 8) {
      if (settleTimerRef.current) {
        clearTimeout(settleTimerRef.current);
        settleTimerRef.current = null;
      }
      setRunReport([...reportRef.current]);
      setRunPhase('halted');
      return;
    }

    // Hold both the empty-check and the fill while the renderer's OS prefill
    // is still writing values — otherwise the run validates a step whose
    // fields are seconds away from filling themselves, and halts falsely.
    if (
      (runPhase === 'probe' || runPhase === 'fill') &&
      isCommendaOSPrefilling &&
      fillRetryCountRef.current < MAX_FILL_RETRIES_PER_STEP
    ) {
      fillRetryCountRef.current += 1;
      safetyCounterRef.current -= 1;
      setTimeout(() => setFillRetryTick((tick) => tick + 1), FILL_RETRY_DELAY_MS);
      return;
    }

    const step = visibleSteps[currentStep];

    if (runPhase === 'probe') {
      expandCurrentStepModules?.();
      const requiredPresence = getRequiredFieldPresence(
        step,
        formData,
        isFieldVisible
      );
      const probeResult = validateCurrentStep();
      let emptyCheck: EmptyCheckOutcome;
      if (!requiredPresence.hasRequired) {
        emptyCheck = 'nothing-required';
      } else if (!requiredPresence.hasEmptyRequired) {
        // Nothing was empty at probe time (e.g. a previous run already filled
        // this step), so the empty-state check proves nothing either way.
        emptyCheck = 'skipped-already-filled';
      } else if (!probeResult.isValid) {
        emptyCheck = 'errors-fired';
      } else {
        emptyCheck = 'no-error-despite-required';
      }
      reportRef.current.push({
        stepIndex: currentStep,
        stepTitle: getStepTitle(step, currentStep),
        emptyCheck,
        filledCount: 0,
        candidateCount: 0,
        filledValues: [],
        skipped: [],
        alreadyFilledCount: 0,
        brokenRequiredRules: findBrokenRequiredRules(step, componentMap),
      });
      setRunPhase('fill');
      return;
    }

    if (runPhase === 'fill') {
      const plan = computeStepPrefill({
        step,
        formData,
        dslContext,
        isFieldVisible,
        isFieldDisabled,
        isPreview,
        hasFillHandler: (candidateFieldId) =>
          Boolean(getPreviewFillHandler?.(candidateFieldId)),
      });
      const executed = executePlan(plan);
      if (
        executed.loadingFields.length > 0 &&
        fillRetryCountRef.current < MAX_FILL_RETRIES_PER_STEP
      ) {
        fillRetryCountRef.current += 1;
        // Retries are waiting, not progress — don't spend the safety budget.
        safetyCounterRef.current -= 1;
        // A stray timeout after stop/halt is harmless: the effect exits unless
        // the phase is still 'fill'.
        setTimeout(() => setFillRetryTick((tick) => tick + 1), FILL_RETRY_DELAY_MS);
        return;
      }
      pendingFilledValuesRef.current = plan.assignments.map((assignment) => ({
        label: findFieldLabelInStep(step, assignment.fieldId) ?? assignment.fieldId,
        value: assignment.value,
      }));
      pendingFillResultRef.current = {
        ...executed,
        // Fields that never finished loading within the retry budget are
        // reported as skips, not silently dropped.
        skipped: [
          ...executed.skipped,
          ...executed.loadingFields.map(({ fieldId, label }) => ({
            fieldId,
            label,
            reason: 'did not finish loading — fill it manually',
          })),
        ],
      };
      setRunPhase('settle');
      return;
    }

    if (runPhase === 'settle') {
      if (!settleTimerRef.current) {
        settleTimerRef.current = setTimeout(() => {
          settleTimerRef.current = null;
          setRunPhase('post-validate');
        }, STEP_SETTLE_MS);
      } else {
        // Effect re-runs during the settle window (cascading value/error
        // commits from the fill) are waiting, not progress — don't let them
        // spend the safety budget.
        safetyCounterRef.current -= 1;
      }
      return;
    }

    // post-validate
    const fillResult = pendingFillResultRef.current;
    const postResult = validateCurrentStep();
    if (!postResult.isValid && !revalidatedRef.current) {
      // First failing pass may have just swept zombie errors — re-judge on
      // the next tick with the cleaned map before treating it as a halt.
      revalidatedRef.current = true;
      safetyCounterRef.current -= 1;
      setTimeout(() => setFillRetryTick((tick) => tick + 1), 50);
      return;
    }
    revalidatedRef.current = false;
    const currentReport = reportRef.current[reportRef.current.length - 1];
    if (currentReport) {
      currentReport.filledCount = fillResult?.filledCount ?? 0;
      currentReport.candidateCount = fillResult?.candidateCount ?? 0;
      currentReport.filledValues = pendingFilledValuesRef.current;
      currentReport.skipped = fillResult?.skipped ?? [];
      currentReport.alreadyFilledCount = fillResult?.alreadyFilledCount ?? 0;
      if (!postResult.isValid) {
        const failedFieldId = postResult.firstErrorField;
        const failedFieldLabel = failedFieldId
          ? findFieldLabelInStep(step, failedFieldId)
          : undefined;
        const errorMessage = failedFieldId
          ? getFieldError?.(failedFieldId)
          : undefined;
        const fieldName = failedFieldLabel ?? failedFieldId ?? 'a field';
        currentReport.haltReason = errorMessage
          ? `"${fieldName}" failed validation: ${errorMessage}`
          : `the "${fieldName}" field has an error — see the form`;
      }
    }
    pendingFillResultRef.current = null;

    if (!postResult.isValid) {
      setRunReport([...reportRef.current]);
      setRunPhase('halted');
      return;
    }

    if (currentStep >= visibleSteps.length - 1) {
      setRunReport([...reportRef.current]);
      setRunPhase('done');
      return;
    }

    setCurrentStep(currentStep + 1);
    setRunPhase('probe');
  }, [
    runPhase,
    fillRetryTick,
    isCommendaOSPrefilling,
    currentStep,
    visibleSteps,
    formData,
    dslContext,
    validateCurrentStep,
    isFieldVisible,
    isFieldDisabled,
    isPreview,
    executePlan,
    getPreviewFillHandler,
    getFieldError,
    expandCurrentStepModules,
    setCurrentStep,
    componentMap,
  ]);

  const currentStepTitle = getStepTitle(visibleSteps[currentStep], currentStep);
  const activityLabel =
    runPhase === 'probe'
      ? `Testing "${currentStepTitle}" — checking empty state...`
      : runPhase === 'fill'
        ? `Testing "${currentStepTitle}" — filling fields...`
        : runPhase === 'settle'
          ? `Testing "${currentStepTitle}" — waiting for fields to settle...`
          : runPhase === 'post-validate'
            ? `Testing "${currentStepTitle}" — validating...`
            : null;

  const runStatus: RunStatus =
    runPhase === 'idle'
      ? 'idle'
      : runPhase === 'done'
        ? 'done'
        : runPhase === 'halted'
          ? 'halted'
          : 'running';

  return {
    fillCurrentStep,
    lastFillResult,
    runToEnd,
    stopRun,
    runStatus,
    runReport,
    activityLabel,
  };
}
